import { useState } from "react";
import { Trophy, Undo2, CheckCircle2, AlertCircle, Heart, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/** Referral leaderboard + win-back email setting, fed by /admin/memberships/insights. */
export function ReferralLeaderboard({ d, onChange }) {
  const [draft, setDraft] = useState(String(d.winback_days));
  const [saving, setSaving] = useState(false);
  const [thanking, setThanking] = useState("");
  const on = d.winback_days > 0;

  const thank = async (r) => {
    setThanking(r.membership_id);
    try { const res = await api.post(`/admin/memberships/${r.membership_id}/thanks`); toast.success(`Thank-you sent to ${res.data.to}`); onChange?.(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Couldn't send"); }
    finally { setThanking(""); }
  };

  const save = async (value) => {
    setSaving(true);
    const fd = new FormData(); fd.append("winback_days", value);
    try { const r = await api.put("/admin/settings", fd); setDraft(String(r.data.winback_days)); toast.success(r.data.winback_days ? `Win-back emails on — ${r.data.winback_days} days after cancelling` : "Win-back emails off"); onChange?.(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };

  return (
    <div className="grid gap-3 lg:grid-cols-2">
      <div className="dg-card rounded-xl p-4" data-testid="referral-leaderboard">
        <div className="dg-eyebrow flex items-center gap-1"><Trophy className="w-3 h-3" /> Referral leaderboard · {d.referred_joins} member{d.referred_joins === 1 ? "" : "s"} joined through referrals</div>
        {d.leaderboard.length === 0
          ? <div className="text-sm text-slate-500 mt-3" data-testid="referral-leaderboard-empty">No referrals yet — every member has a share link on their membership page.</div>
          : <ol className="mt-3 space-y-2 text-sm">
            {d.leaderboard.map((r, i) => (
              <li key={r.membership_id} className="flex flex-wrap items-center gap-2" data-testid={`leader-row-${r.membership_id}`}>
                <span className={`w-6 h-6 rounded-full grid place-items-center text-xs font-bold ${i === 0 ? "bg-[#FFD600] text-slate-900" : "bg-slate-800 text-slate-300"}`}>{i + 1}</span>
                <span className="font-medium">{r.name}</span>
                <a href={`mailto:${r.email}`} className="dg-link text-xs">{r.email}</a>
                {!r.live && <span className="text-xs text-slate-500">(not active)</span>}
                <span className="ml-auto text-xs text-slate-400">{r.referrals} friend{r.referrals === 1 ? "" : "s"} · {r.free_months} free month{r.free_months === 1 ? "" : "s"}{r.pending_credits ? ` · ${r.pending_credits} to apply` : ""}</span>
                {r.thanks_due
                  ? <button type="button" onClick={() => thank(r)} disabled={thanking === r.membership_id} className="dg-btn-ghost rounded-full px-3 py-1 text-xs flex items-center gap-1 disabled:opacity-50" data-testid={`leader-thanks-${r.membership_id}`}>
                    {thanking === r.membership_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Heart className="w-3 h-3" />} {r.thanks_sent_at ? "Thank again" : "Send thanks"}
                  </button>
                  : <span className="dg-chip text-xs" data-testid={`leader-thanked-${r.membership_id}`}><Heart className="w-3 h-3" /> Thanked {new Date(r.thanks_sent_at).toLocaleDateString()}</span>}
                {r.friends.length > 0 && <div className="w-full text-xs text-slate-500 pl-8">Brought in: {r.friends.join(", ")}</div>}
              </li>
            ))}
          </ol>}
        <p className="text-xs text-slate-500 mt-3">A personal thank-you goes a long way — top referrers are your best marketing.</p>
      </div>

      <div className="dg-card rounded-xl p-4" data-testid="admin-winback">
        <div className="flex items-center gap-2">
          {on ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> : <AlertCircle className="w-4 h-4 text-amber-400" />}
          <div className="dg-eyebrow flex items-center gap-1"><Undo2 className="w-3 h-3" /> Win-back email</div>
          <span className="dg-chip ml-auto" data-testid="winback-status-chip">{on ? `On · ${d.winback_days} days after` : "Off"}</span>
        </div>
        <p className="text-sm text-slate-400 mt-2">One friendly "everything still working?" note with a one-tap Rejoin button, sent this many days after a membership ends — skipped if they've already rejoined. One per former member. {d.winback_sent} sent · {d.winback_rejoined} rejoined.</p>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <input type="number" min="0" max="365" className="dg-input w-24 text-sm" value={draft} onChange={(e) => setDraft(e.target.value)} data-testid="winback-days-input" />
          <span className="text-sm text-slate-400">days after cancelling</span>
          <button type="button" onClick={() => save(draft)} disabled={saving} className="dg-btn-primary rounded-full px-5 py-2 text-sm disabled:opacity-50" data-testid="winback-save">Save</button>
          <button type="button" onClick={() => save(on ? 0 : 30)} disabled={saving} className="dg-btn-ghost rounded-full px-5 py-2 text-sm disabled:opacity-50" data-testid="winback-toggle">{on ? "Turn off" : "Turn on (30 days)"}</button>
        </div>
      </div>
    </div>
  );
}
